import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { BiError, BiCheck } from "react-icons/bi";
import { Label, TextInput, Button, Alert } from "flowbite-react";
import { Helmet } from "react-helmet-async";
import { newPassword } from "services/newPassword.service";
import "./styles.scss";

const newPasswordSchema = z.object({
  email: z
    .string()
    .min(1, "O email é obrigatório")
    .email("Digite um email válido"),
  password: z
    .string()
    .min(6, "A senha deve ter pelo menos 6 caracteres")
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, "A senha deve conter pelo menos: 1 letra minúscula, 1 maiúscula e 1 número"),
  confirmPassword: z.string()
}).refine(data => data.password === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"]
});


interface IFormInput {
  email: string;
  password: string;
  confirmPassword: string;
}

export function NewPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlEmail = searchParams.get('email') || '';
  
  const { register, handleSubmit, watch, formState: { errors } } = useForm<IFormInput>({
    defaultValues: {
      email: urlEmail, 
      password: '',
      confirmPassword: ''
    }
  });
  
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const email = watch('email', '');
  const password = watch('password', '');
  const confirmPassword = watch('confirmPassword', '');

  // Validação em tempo real
  const validatePasswords = (data: IFormInput) => {
    try {
      newPasswordSchema.parse(data);
      setErrorMessage("");
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.issues[0];
        setErrorMessage(firstError.message);
      }
      return false;
    }
  };

  const onSubmit = async (data: IFormInput) => {
    if (!validatePasswords(data)) {
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");

      await newPassword(data.email, data.password);

      setSuccess(true);
      
      setTimeout(() => {
        navigate("/login", { 
          state: { message: "Senha redefinida com sucesso! Faça login com sua nova senha." }
        });
      }, 3000); 

    } catch (error: any) {
      setErrorMessage(error.message || "Erro ao redefinir senha. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const onSubmitError = (formErrors: any) => {
    const firstError = Object.values(formErrors)[0] as any;
    if (firstError?.message) {
      setErrorMessage(firstError.message);
    }
  };

  // Validações visuais em tempo real
  const hasMinLength = password.length >= 6;
  const hasLowerCase = /[a-z]/.test(password);
  const hasUpperCase = /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  if (success) {
    return (
      <>
        <Helmet>
          <title>Senha Redefinida - Rankifier</title>
        </Helmet>
        <div
          id="forgot-wrapper"
          className="w-full min-h-screen flex items-center justify-center"
        >
          <div className="forgot-card">
            <div className="forgot-header">
              <div className="success-icon">
                <BiCheck size={48} className="text-green-500" />
              </div>
              <h1 className="forgot-title">Senha redefinida com sucesso!</h1>
              <p className="forgot-subtitle">
                Sua senha foi alterada. Você será redirecionado para a página de login em alguns segundos.
              </p>
            </div>
            
            <div className="forgot-footer">
              <Link to="/login" className="back-to-login">
                Ir para login agora
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Nova Senha - Rankifier</title>
      </Helmet>
      <div
        id="forgot-wrapper"
        className="w-full min-h-screen flex items-center justify-center"
      >
        <div className="forgot-card">
          <div className="forgot-header">
            <h1 className="forgot-title">Definir nova senha</h1>
            <p className="forgot-subtitle">
              Crie uma nova senha segura para sua conta.
            </p>
          </div>

          <form 
            className="forgot-form" 
            onSubmit={handleSubmit(onSubmit, onSubmitError)}
          >
            <div className="form-group">
              <Label htmlFor="email" className="form-label">Email</Label>
              {/* Informational display: show the email that will receive the new password */}
              <div className="form-input readonly-display" aria-live="polite">
                {urlEmail || <span className="text-muted">Nenhum email informado</span>}
              </div>
              {/* Hidden registered input so react-hook-form keeps email value for submit/validation */}
              <input
                type="hidden"
                {...register("email", {
                  required: "O email é obrigatório",
                })}
                value={urlEmail}
                readOnly
              />
            </div>

            <div className="form-group">
              <Label htmlFor="password" className="form-label">Nova senha</Label>
              <TextInput
                {...register("password", { 
                  required: "A senha é obrigatória",
                  onChange: () => setErrorMessage("") 
                })}
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                placeholder="••••••••"
                className="form-input"
              />
              
              {/* Indicadores visuais da força da senha */}
              {password && (
                <div className="password-strength mt-2">
                  <div className="strength-indicators">
                    <div className={`strength-item ${hasMinLength ? 'valid' : 'invalid'}`}>
                      <BiCheck size={16} className={hasMinLength ? 'text-green-500' : 'text-gray-400'} />
                      <span>Pelo menos 6 caracteres</span>
                    </div>
                    <div className={`strength-item ${hasLowerCase ? 'valid' : 'invalid'}`}>
                      <BiCheck size={16} className={hasLowerCase ? 'text-green-500' : 'text-gray-400'} />
                      <span>Uma letra minúscula</span>
                    </div>
                    <div className={`strength-item ${hasUpperCase ? 'valid' : 'invalid'}`}>
                      <BiCheck size={16} className={hasUpperCase ? 'text-green-500' : 'text-gray-400'} />
                      <span>Uma letra maiúscula</span>
                    </div>
                    <div className={`strength-item ${hasNumber ? 'valid' : 'invalid'}`}>
                      <BiCheck size={16} className={hasNumber ? 'text-green-500' : 'text-gray-400'} />
                      <span>Um número</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <Label htmlFor="confirmPassword" className="form-label">Confirmar nova senha</Label>
              <TextInput
                {...register("confirmPassword", { 
                  required: "A confirmação da senha é obrigatória",
                  onChange: () => setErrorMessage("") 
                })}
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                placeholder="••••••••"
                className="form-input"
              />
              
              {/* Indicador de senhas coincidentes */}
              {confirmPassword && (
                <div className={`match-indicator mt-2 ${passwordsMatch ? 'valid' : 'invalid'}`}>
                  <BiCheck size={16} className={passwordsMatch ? 'text-green-500' : 'text-red-500'} />
                  <span>{passwordsMatch ? 'As senhas coincidem' : 'As senhas não coincidem'}</span>
                </div>
              )}
            </div>

            {!!errorMessage && (
              <Alert color="failure" icon={BiError}>
                {errorMessage}
              </Alert>
            )}

            <Button
              type="submit"
              disabled={loading || !email || !password || !confirmPassword}
              className="forgot-button"
            >
              {loading ? "Definindo..." : "Definir nova senha"}
            </Button>

            <div className="forgot-footer">
              <Link to="/login" className="back-to-login">
                Voltar ao login
              </Link>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
